'use client'
import { useSession } from 'next-auth/react'
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { api } from '@/api/api'
import type { FacilityReport } from '@/models/types/report/facility-report'
import { FacilityReportStatus } from '@/models/types/report/facility-report-state'
import type { Report } from '@/models/types/report/report'
import { type ReportType } from '@/models/types/report/report-type'
import type { ReportAction } from '@/types/signal-r-actions'
import { useToaster, type ToastData } from '../toast/toasts-context'
import { useSignalR } from './signal-r-provider'

type ReportsContextProps = {
    generateFacilityReport: (
        reportType: ReportType,
        selectedFacility: number,
        fromDate: Date,
        toDate: Date,
        description?: string,
    ) => Promise<FacilityReport>
}

const ReportsProviderContext = createContext<ReportsContextProps>({
    generateFacilityReport: async (reportType: ReportType, selectedFacility: number, fromDate: Date, toDate: Date) => {
        return {
            id: 0,
            reportType,
            facilityId: selectedFacility,
            fromDate,
            toDate,
            status: FacilityReportStatus.InProgress,
            createdAt: new Date(),
            updatedAt: new Date(),
            requestDate: new Date().toISOString(),
            creationDate: new Date().toISOString(),
            type: reportType,
        }
    },
})

type ReportsProviderProps = {
    children?: ReactNode
}

type Notification = { report: Report; description?: string | undefined; update: (toast: ToastData) => void }

function ReportsProvider(props: ReportsProviderProps): JSX.Element {
    const [notifications, setNotifications] = useState<Notification[]>([])
    const toaster = useToaster()
    const session = useSession()
    const enabled = session.status === 'authenticated'
    const { subscribe, unsubscribe } = useSignalR()

    const generateFacilityReport = useCallback(
        async (
            reportType: ReportType,
            selectedFacility: number,
            fromDate: Date,
            toDate: Date,
            description?: string,
        ): Promise<FacilityReport> => {
            return api()
                .facility()
                .generateReport(reportType, selectedFacility, fromDate.toISOString(), toDate.toISOString())
                .then((response) => {
                    const { update } = toaster.add({
                        content: (
                            <div>
                                <div className="font-semibold">Generando il report...</div>
                                {description ? <div className="text-sm">{description}</div> : null}
                            </div>
                        ),
                        type: 'info',
                        variant: 'simple',
                    })
                    const newNotification: Notification = { report: response, description, update }
                    setNotifications((prev) => [...prev, newNotification])
                    return response
                })
                .catch((error: unknown) => {
                    toaster.add({
                        content: ['Errore durante la generazione del report', error].join(' | '),
                        type: 'error',
                        variant: 'solid',
                        timeout: 5000,
                    })
                    throw error
                })
        },
        [toaster],
    )

    const closeNotificationWithSuccess = useCallback(
        (id: number) => {
            const notification = notifications.find((ns) => ns.report.id === id)
            if (!notification) return
            const { update, description } = notification

            update({
                content: (
                    <>
                        <div className="font-semibold">Report generato con successo.</div>
                        {description ? <div className="text-sm">{description}</div> : null}
                    </>
                ),
                type: 'info',
                variant: 'simple',
                timeout: 5000,
            })
            setNotifications((prev) => prev.filter((ns) => ns.report.id !== id))
        },
        [notifications],
    )

    const closeNotificationWithError = useCallback(
        (id: number) => {
            const notification = notifications.find((ns) => ns.report.id === id)
            if (!notification) return
            const { update, description } = notification

            update({
                content: (
                    <>
                        <div className="font-semibold">Errore durante la generazione del report.</div>
                        {description ? <div className="text-sm">{description}</div> : null}
                    </>
                ),
                type: 'error',
                variant: 'solid',
                timeout: 5000,
            })
            setNotifications((prev) => prev.filter((ns) => ns.report.id !== id))
        },
        [notifications],
    )

    const handleReports = useCallback(
        (serializedData: unknown): void => {
            const parsedData = serializedData as string[]

            const [_topic, serializedReportAction] = parsedData
            const reportAction = JSON.parse(serializedReportAction) as ReportAction

            switch (reportAction.statoReport) {
                case FacilityReportStatus.Error:
                    closeNotificationWithError(reportAction.reportId)
                    break
                case FacilityReportStatus.InProgress:
                    break
                case FacilityReportStatus.Ready:
                    closeNotificationWithSuccess(reportAction.reportId)
                    break
            }
        },
        [closeNotificationWithError, closeNotificationWithSuccess],
    )

    useEffect(() => {
        if (!enabled) return

        subscribe('ReportAction', handleReports)

        return () => {
            unsubscribe('Reports', handleReports)
        }
    }, [enabled, handleReports, subscribe, unsubscribe])

    return (
        <ReportsProviderContext.Provider value={{ generateFacilityReport }}>
            {props.children}
        </ReportsProviderContext.Provider>
    )
}

const useReports = (): ReportsContextProps => {
    const context = useContext(ReportsProviderContext)
    return context
}

export { ReportsProvider, useReports }
